import React, { useEffect, useRef, useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import createDatafeed from "./tvDatafeed";
import { setChartWidget } from "./chartToolBridge";

function collectRows(m) {
  if (!m) return [];
  if (m instanceof Map) return Array.from(m.values());
  if (Array.isArray(m)) return m;
  if (typeof m === "object") return Object.values(m);
  return [];
}

function getSymbolFromProps(symbol, selectedRow) {
  const s = String(symbol ?? "").trim();
  if (s) return s;
  return String(
    selectedRow?.script ?? selectedRow?.displayName ?? selectedRow?.symbol ?? selectedRow?.name ?? ""
  ).trim();
}

const STRIP_CSS = `
  #drawing-toolbar,
  [data-name="toolbar-drawing-toggle-button"] {
    display: none !important;
  }
  *:has(> #drawing-toolbar) {
    width: 0 !important;
    min-width: 0 !important;
    max-width: 0 !important;
    flex: 0 0 0px !important;
    overflow: hidden !important;
    padding: 0 !important;
    margin: 0 !important;
    border: none !important;
    background: transparent !important;
  }
  .chart-gui-wrapper {
    padding-inline-start: 0 !important;
  }
`;

/** Inject strip-hiding CSS as soon as the iframe document exists (before chart-ready). */
function injectStripCssEarly(hostEl) {
  const iframe = hostEl?.querySelector?.("iframe");
  const doc = iframe?.contentDocument;
  if (!doc?.head) return false;
  if (doc.getElementById("fx-remove-toolbar-strip")) return true;
  const style = doc.createElement("style");
  style.id = "fx-remove-toolbar-strip";
  style.textContent = STRIP_CSS;
  doc.head.appendChild(style);
  return true;
}

/**
 * Remove the native left-toolbar column completely (not leave a 5px collapse).
 * ChartToolRail replaces that UI; selectLineTool still needs the toolbar mounted.
 */
function removeNativeToolbarStrip(hostEl) {
  const iframe = hostEl?.querySelector?.("iframe");
  const doc = iframe?.contentDocument;
  if (!doc?.body) return false;

  injectStripCssEarly(hostEl);

  const tb = doc.getElementById("drawing-toolbar");
  if (!tb) return false;

  // Drive layout width WV to 0 (collapsed is still 5px — that leftover strip).
  const bridge = findResizerBridge(tb);
  if (bridge?.negotiateWidth) {
    try { bridge.negotiateWidth(0); } catch {}
  }

  // Find the narrow column the library sizes via style.width (5px or 52px).
  let slot = null;
  for (let node = tb.parentElement; node && node !== doc.body; node = node.parentElement) {
    const rectW = node.getBoundingClientRect().width;
    const styleW = node.style.width;
    const isKnown =
      styleW === "5px" ||
      styleW === "52px" ||
      styleW === "0px" ||
      (rectW > 0 && rectW <= 56);
    if (!isKnown) break;
    const parent = node.parentElement;
    const parentW = parent?.getBoundingClientRect().width ?? 0;
    // Stop before the wide layout row that also holds the chart.
    if (parentW > 120 && parentW > rectW * 2) {
      slot = node;
      break;
    }
    slot = node;
  }
  if (!slot) slot = tb.parentElement;
  if (!slot) return false;

  const kill = () => {
    if (slot.dataset.fxKilling === "1") return;
    slot.dataset.fxKilling = "1";
    try {
      try { bridge?.negotiateWidth?.(0); } catch {}
      slot.style.setProperty("width", "0px", "important");
      slot.style.setProperty("min-width", "0px", "important");
      slot.style.setProperty("max-width", "0px", "important");
      slot.style.setProperty("flex", "0 0 0px", "important");
      slot.style.setProperty("padding", "0", "important");
      slot.style.setProperty("margin", "0", "important");
      slot.style.setProperty("border", "none", "important");
      slot.style.setProperty("overflow", "hidden", "important");
      slot.style.setProperty("background", "transparent", "important");
    } finally {
      slot.dataset.fxKilling = "0";
    }
  };
  kill();

  // Library may write style.width back to 5/52 — keep it at 0.
  if (!slot.dataset.fxStripObs) {
    slot.dataset.fxStripObs = "1";
    const mo = new MutationObserver(kill);
    mo.observe(slot, { attributes: true, attributeFilter: ["style"] });
  }

  return true;
}

/** Walk React fiber from #drawing-toolbar up to resizerBridge.negotiateWidth. */
function findResizerBridge(domNode) {
  const key = Object.keys(domNode).find(
    (k) => k.startsWith("__reactFiber$") || k.startsWith("__reactInternalInstance$")
  );
  if (!key) return null;
  let fiber = domNode[key];
  for (let i = 0; i < 40 && fiber; i += 1) {
    const props = fiber.memoizedProps || fiber.pendingProps;
    if (props?.resizerBridge?.negotiateWidth) return props.resizerBridge;
    fiber = fiber.return;
  }
  return null;
}

export default function TvChartWidget({
  symbol,
  selectedRow,
  darkMode = false,
  viewOnly = false,
}) {
  const containerRef = useRef(null);
  const widgetRef = useRef(null);
  const [boxReady, setBoxReady] = useState(false);

  const marketWatchList = useSelector((s) => s.marketWatch?.marketWatchList);
  const scriptIndexList = useSelector((s) => s.marketWatch?.scriptIndexList);

  const symbolKey = useMemo(
    () => getSymbolFromProps(symbol, selectedRow),
    [symbol, selectedRow]
  );

  const symbolKeyRef = useRef(symbolKey);
  symbolKeyRef.current = symbolKey;

  const datafeed = useMemo(
    () => createDatafeed(() => symbolKeyRef.current),
    []
  );

  const checkBox = useCallback(() => {
    const el = containerRef.current;
    if (!el) return false;
    const r = el.getBoundingClientRect();
    if (r.width > 50 && r.height > 50) {
      setBoxReady(true);
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (boxReady) return undefined;
    if (checkBox()) return undefined;
    const ro = new ResizeObserver(() => checkBox());
    if (containerRef.current) ro.observe(containerRef.current);
    const timers = [50, 200, 600, 1200].map((ms) => setTimeout(checkBox, ms));
    return () => {
      ro.disconnect();
      timers.forEach(clearTimeout);
    };
  }, [boxReady, checkBox]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !symbolKey || !boxReady || !window.TradingView?.widget) return;

    const w = new window.TradingView.widget({
      container: el,
      datafeed,
      symbol: symbolKey,
      interval: "1",
      library_path: `${process.env.PUBLIC_URL || ""}/charting_library/`,
      locale: "en",
      fullscreen: false,
      autosize: true,
      theme: darkMode ? "dark" : "light",
      timezone: "Asia/Kolkata",
      disabled_features: [
        "use_localstorage_for_settings",
        "header_widget",
        "timeframes_toolbar",
      ],
      // Keep left_toolbar mounted for selectLineTool(); strip is removed in the
      // iframe after ready. Start hidden so we fight 5px not 52px.
      enabled_features: ["hide_left_toolbar_by_default"],
      custom_css_url: `${process.env.PUBLIC_URL || ""}/charting_library/fx-chart-custom.css`,
      overrides: {
        "mainSeriesProperties.style": 1,
        "mainSeriesProperties.candleStyle.upColor": "#089981",
        "mainSeriesProperties.candleStyle.downColor": "#f23645",
        "mainSeriesProperties.candleStyle.wickUpColor": "#089981",
        "mainSeriesProperties.candleStyle.wickDownColor": "#f23645",
        "mainSeriesProperties.candleStyle.borderUpColor": "#089981",
        "mainSeriesProperties.candleStyle.borderDownColor": "#f23645",
        "mainSeriesProperties.candleStyle.drawWick": true,
        "mainSeriesProperties.candleStyle.drawBorder": true,
      },
      loading_screen: {
        backgroundColor: darkMode ? "#131722" : "#ffffff",
        foregroundColor: darkMode ? "#787b86" : "#2962ff",
      },
    });

    widgetRef.current = w;

    let cancelled = false;
    let poll = null;
    let earlyPoll = null;
    let iframeMo = null;

    // Inject strip-hiding CSS the moment the iframe document exists — do not wait
    // for onChartReady, or the 5px column flashes on refresh.
    const tryEarlyCss = () => {
      if (cancelled) return;
      if (injectStripCssEarly(el) && earlyPoll) {
        clearInterval(earlyPoll);
        earlyPoll = null;
      }
    };
    earlyPoll = setInterval(tryEarlyCss, 50);
    try {
      iframeMo = new MutationObserver(tryEarlyCss);
      iframeMo.observe(el, { childList: true, subtree: true });
    } catch {}
    tryEarlyCss();

    try {
      w.onChartReady(() => {
        setChartWidget(w);

        try {
          const chart = w.activeChart?.();
          if (chart?.getCheckableActionState?.("drawingToolbarAction")) {
            chart.executeActionById("drawingToolbarAction");
          }
        } catch {}

        const run = () => {
          if (cancelled) return;
          if (removeNativeToolbarStrip(el)) {
            try { window.dispatchEvent(new Event("resize")); } catch {}
            try { w.resize?.(); } catch {}
          }
        };

        run();
        let n = 0;
        poll = setInterval(() => {
          run();
          n += 1;
          if (n >= 16) {
            clearInterval(poll);
            poll = null;
          }
        }, 250);
      });
    } catch {}

    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
      if (earlyPoll) clearInterval(earlyPoll);
      if (iframeMo) iframeMo.disconnect();
      if (widgetRef.current === w) {
        setChartWidget(null);
        try { w.remove(); } catch {}
        widgetRef.current = null;
      }
    };
  }, [symbolKey, darkMode, viewOnly, datafeed, boxReady]);

  useEffect(() => {
    if (!symbolKey) return;
    const findRow = () => {
      if (selectedRow && typeof selectedRow === "object") return selectedRow;
      const key = symbolKey.toUpperCase();
      const rows = [...collectRows(marketWatchList), ...collectRows(scriptIndexList)];
      return rows.find((r) => {
        const s = String(r?.script ?? r?.displayName ?? r?.symbol ?? "").toUpperCase();
        return s === key;
      });
    };
    const row = findRow();
    if (!row) return;
    const ltp = Number(row.lastTradedPrice ?? row.ltp);
    if (!Number.isFinite(ltp) || ltp <= 0) return;
    datafeed.pushLiveTick(symbolKey, ltp, Number(row.volume ?? row.Volume) || 0);
  }, [marketWatchList, scriptIndexList, symbolKey, selectedRow, datafeed]);

  return <div ref={containerRef} className="tv-charting-widget" />;
}
